import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import PubSub from "pubsub-js";
import { pubsubEvents } from "../helpers/socket/pubsubEvent";

export type AlertStatus = "PENDING" | "ACTIVE" | "FAILED";

interface AlertState {
  marketDataSubscribed: boolean;
  queryDeployed: boolean;
  status: AlertStatus;
  message?: string;
  timestamp: number;
}

interface AlertingStatusContextType {
  alerts: Record<string, AlertState>; // Map of reqId to state
  getAlertStatus: (reqId: string) => AlertStatus;
}

const AlertingStatusContext = createContext<
  AlertingStatusContextType | undefined
>(undefined);

// Cleanup timeout: 5 minutes
const CLEANUP_TIMEOUT = 5 * 60 * 1000;

export const AlertingStatusProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [alerts, setAlerts] = useState<Record<string, AlertState>>({});

  const cleanupOldAlerts = useCallback(() => {
    const now = Date.now();
    setAlerts((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      Object.keys(newState).forEach((reqId) => {
        if (now - newState[reqId].timestamp > CLEANUP_TIMEOUT) {
          delete newState[reqId];
          hasChanges = true;
        }
      });
      return hasChanges ? newState : prev;
    });
  }, []);

  useEffect(() => {
    const token = PubSub.subscribe(
      pubsubEvents.stompSubscriptionKeys.ALERTING_STATUS_UPDATE,
      (msg, data) => {
        const { reqId, type, status, message, reason } = data;

        setAlerts((prev) => {
          const currentState = prev[reqId] || {
            marketDataSubscribed: false,
            queryDeployed: false,
            status: "PENDING",
            timestamp: Date.now(),
          };
          const newState = { ...currentState, timestamp: Date.now() };
          const normalizedStatus = String(status || "").toLowerCase();

          if (
            normalizedStatus === "failed" ||
            normalizedStatus === "failure" ||
            normalizedStatus === "error"
          ) {
            newState.status = "FAILED";
            newState.message = message || reason;
            PubSub.publish(pubsubEvents.errorKeys.CORE_ERROR_TOAST, {
              errors: [`Alert failed: ${message || reason || "Unknown error"}`],
            });
          } else if (normalizedStatus === "pending") {
            newState.status = "PENDING";
            newState.message =
              message || reason || "System unavailable, retrying...";
            newState.marketDataSubscribed = false;
            newState.queryDeployed = false;

            PubSub.publish(
              pubsubEvents.successKeys.TRADING_SERVICE_SUCCESS_TOAST,
              newState.message,
            );

            // // Trigger a refresh of the alerts list to ensure UI is in sync with backend status
            // PubSub.publish(pubsubEvents.successKeys.ALERT_NOTIFICATION_TOAST);
          } else if (
            normalizedStatus === "active" ||
            normalizedStatus === "success"
          ) {
            if (type === "marketdata_subscription") {
              newState.marketDataSubscribed = true;
            } else if (type === "query_deployment") {
              newState.queryDeployed = true;
            }

            if (
              newState.marketDataSubscribed &&
              newState.queryDeployed &&
              currentState.status !== "ACTIVE"
            ) {
              newState.status = "ACTIVE";

              const isEdit =
                sessionStorage.getItem(`alert_edit_${reqId}`) === "true" ||
                sessionStorage.getItem(`alert_edit_toast_${reqId}`) === "true";
              const isResume =
                sessionStorage.getItem(`alert_resume_${reqId}`) === "true" ||
                sessionStorage.getItem(`alert_resume_toast_${reqId}`) ===
                  "true";
              if (!isEdit && !isResume) {
                PubSub.publish(
                  pubsubEvents.successKeys.TRADING_SERVICE_SUCCESS_TOAST,
                  "Alert created successfully!",
                );
              }
            }
          }

          return { ...prev, [reqId]: newState };
        });
      },
    );

    // Run cleanup every minute
    const cleanupInterval = setInterval(cleanupOldAlerts, 60000);

    return () => {
      PubSub.unsubscribe(token);
      clearInterval(cleanupInterval);
    };
  }, [cleanupOldAlerts]);

  const getAlertStatus = (reqId: string): AlertStatus => {
    return alerts[reqId]?.status || "PENDING";
  };

  return (
    <AlertingStatusContext.Provider value={{ alerts, getAlertStatus }}>
      {children}
    </AlertingStatusContext.Provider>
  );
};

export const useAlertingStatus = () => {
  const context = useContext(AlertingStatusContext);
  if (context === undefined) {
    throw new Error(
      "useAlertingStatus must be used within an AlertingStatusProvider",
    );
  }
  return context;
};
